
import { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';

import {
  classificationNormalAccuracyQuery,
  consolidatedInsightsQuery,
  generalInfoQuery,
} from '../api';

import './styles.css';

import { navigation } from '../data';
import SubPagesLayout from '../../../UI/layout/subPages';
import PopupMessages from '../../../UI/introGameLayout/popupMessages';
import Scores from '../../../UI/game/scores';
import TopMisclassifiedAccountsModal from '../../../UI/instructor/decisionInsights/topMisclassifiedAccountsModal';

import {
  classifyingNormalSections,
  popupMessagesDecisionInsightsClassifyingNormal,
  popupMessagesDecisionInsightsNoGamesData,
} from './data';

export default function InstructorInsightsClassifyingNormal() {
  const navigate = useNavigate();
  const location = useLocation();

  // Sibling pages hand us the travel direction so a skipped page can
  // keep moving the same way. Default forward when opened directly.
  const direction = location.state?.direction === 'back' ? 'back' : 'forward';

  const [isModalShown, setIsModalShown] = useState(false);
  const [isDetailsButtonLoading, setIsDetailsButtonLoading] = useState(false);
  const [shouldFetchDetails, setShouldFetchDetails] = useState(false);
  const [filterBy, setFilterBy] = useState('Accuracy');

  const [popupMessages, setPopupMessages] = useState([]);
  const [detailsButtonText, setDetailsButtonText] = useState(null);
  const [labels, setLabels] = useState([]);

  const [data, setData] = useState([]);
  const [classificationInsights, setClassificationInsights] = useState([]);
  const [normalInsights, setNormalInsights] = useState([]);

  const filterByOptions = ['Accuracy', 'Points'];

  // Pre-computed Classification + Normal Balance accuracy averages
  // from ?classification_normal&average_accuracy — flat object with
  // one value per category, replacing the full per-session payload.
  const { data: accuracyData } = useQuery(classificationNormalAccuracyQuery());
  const { data: consolidatedInsights } = useQuery({
    ...consolidatedInsightsQuery(),
    enabled: shouldFetchDetails,
  });
  // sessions carry each attempt's level_names — the authoritative signal
  // for which decision-type levels are actually configured in this
  // course. Chart bars + page visibility below gate off these.
  const { data: generalInfo } = useQuery(generalInfoQuery()) || {};

  const classificationAccuracy = accuracyData?.classification || {};
  const normalBalanceAccuracy = accuracyData?.normal_balance || {};

  // Section-visibility gates. Each returns true iff at least one
  // completed session in this course used the matching level name.
  // Elements → class-elements / norm-elements.
  // Various Accounts + Tricky Accounts → class-default / norm-default.
  const sessions = generalInfo?.sessions;
  const hasLevel = useMemo(() => {
    if (!sessions) return null; // null = still loading
    const namesInPlay = new Set();
    sessions.forEach(session => {
      Object.values(session.level_names || {}).forEach(name => namesInPlay.add(name));
    });
    return {
      classElements: namesInPlay.has('class-elements'),
      normElements: namesInPlay.has('norm-elements'),
      classDefault: namesInPlay.has('class-default'),
      normDefault: namesInPlay.has('norm-default'),
    };
  }, [sessions]);

  useEffect(() => {
    if (accuracyData && hasLevel) {
      formatLeaderboardData();
    }
    // formatLeaderboardData reads hasLevel + accuracy from closure;
    // reruns whenever either changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accuracyData, filterBy, hasLevel]);

  const formatLeaderboardData = () => {
    // Per-section line-visibility rule (see comment on hasLevel):
    // - Elements section: classification line iff class-elements
    //   configured, normal line iff norm-elements configured.
    // - Other sections: classification line iff class-default,
    //   normal line iff norm-default.
    // A bar with zero visible lines is kept (so the section grid
    // stays aligned), and empty sections drop out at the .filter
    // step below.
    const showClassification = (sectionIndex) =>
      sectionIndex === 0 ? hasLevel.classElements : hasLevel.classDefault;
    const showNormal = (sectionIndex) =>
      sectionIndex === 0 ? hasLevel.normElements : hasLevel.normDefault;

    const data = classifyingNormalSections
      .map((section, sectionIndex) => {
        const includeClass = showClassification(sectionIndex);
        const includeNorm = showNormal(sectionIndex);
        return {
          title: section.title,
          bars: section.bars.map(bar => {
            const classificationValue = classificationAccuracy[bar.key];
            const normalValue = normalBalanceAccuracy[bar.key];
            const lines = [];
            if (includeClass) {
              lines.push({ score: classificationValue != null ? classificationValue * 100 : 0, color: '#D7F0F2' });
            }
            if (includeNorm) {
              lines.push({ score: normalValue != null ? normalValue * 100 : 0, color: '#FFF8EB' });
            }
            return { title: bar.title, lines };
          }),
          barsLabel: '%'
        };
      })
      // Section is displayable if any bar carries at least one line —
      // driven purely by level configuration now, not by score presence.
      .filter(section => section.bars.some(bar => bar.lines.length > 0));

    setData(data);
  }

  // If every section is hidden (no class-elements / norm-elements /
  // class-default / norm-default configured in this course), skip the
  // page entirely and forward to the sibling in the direction the user
  // was traveling. Guarded so we only redirect once per mount.
  const skippedRef = useRef(false);
  useEffect(() => {
    if (skippedRef.current) return;
    if (!accuracyData || !hasLevel) return;
    const anySectionVisible =
      hasLevel.classElements || hasLevel.normElements
      || hasLevel.classDefault || hasLevel.normDefault;
    if (anySectionVisible) return;
    skippedRef.current = true;
    const target = direction === 'back'
      ? '/instructor/insights/decision-insights/overall'
      : '/instructor/insights/decision-insights/classified-multistep';
    navigate(target, { replace: true, state: { direction } });
  }, [accuracyData, hasLevel, direction, navigate]);

  // Gate the popup + legend labels off level configuration. The legend
  // only lists a game type if that game's chart lines will actually
  // appear somewhere on the page (per hasLevel above), so instructors
  // don't see a "Normal" swatch when no Normal Balance bars render.
  useEffect(() => {
    if (!accuracyData || !hasLevel) return;
    const anyClassification = hasLevel.classElements || hasLevel.classDefault;
    const anyNormal = hasLevel.normElements || hasLevel.normDefault;
    const hasChartData = anyClassification || anyNormal;
    if (hasChartData) {
      setPopupMessages(popupMessagesDecisionInsightsClassifyingNormal);
      setDetailsButtonText('View Details');
      const nextLabels = [];
      if (anyClassification) nextLabels.push({ name: 'Classification', color: '#D7F0F2' });
      if (anyNormal) nextLabels.push({ name: 'Normal', color: '#FFF8EB' });
      setLabels(nextLabels);
    } else {
      setPopupMessages(popupMessagesDecisionInsightsNoGamesData);
    }
  }, [accuracyData, hasLevel]);

  // Ranking + section filtering now live inside
  // TopMisclassifiedAccountsModal — we hand it the raw insights and it
  // computes the top-10 with the 20% threshold + element-priority rule.
  useEffect(() => {
    if (consolidatedInsights && shouldFetchDetails) {
      setClassificationInsights(consolidatedInsights.classification || []);
      setNormalInsights(consolidatedInsights.normal_balance || []);
      setIsModalShown(true);
      setIsDetailsButtonLoading(false);
    }
  }, [consolidatedInsights, shouldFetchDetails]);

  const handlePrevButtonClick = () => {
    navigate('/instructor/insights/decision-insights/overall');
  }

  const handleNextButtonClick = () => {
    // Always slot the classified-multistep breakdown directly after
    // classifying-normal — both pages cover Account Classification
    // content, so they read as a pair. The downstream routing
    // (double-entry/principles/progress) is handled on that page.
    // Pass direction so multistep can keep travelling forward if it
    // has nothing to show.
    navigate(
      '/instructor/insights/decision-insights/classified-multistep',
      { state: { direction: 'forward' } }
    );
  }

  const handleFilterClick = (selectedFilterBy) => {
    setFilterBy(selectedFilterBy);
  }

  const maxScore = Math.max(...data.flatMap(section =>
    section.bars?.flatMap(bar =>
      bar.lines?.map(line => line.score) || []
    ) || []
  ));

  const handleDetailsButtonClick = () => {
    setIsDetailsButtonLoading(true);
    setShouldFetchDetails(true);
  }

  const handleModalCloseButtonClick = () => {
    setIsModalShown(false);
  }

  return (
    <SubPagesLayout
      navigation={navigation}
      onFirstButtonClick={handlePrevButtonClick}
      onSecondButtonClick={handleNextButtonClick}
      currentPage={2}
      pagesQuantity={5}
      contentClassName="decision-insights-content-container instructor-decision-insights-content-container"
    >
      <h1>INSIGHTS | OVERALL <b>| Account Classification & Normal Balances</b></h1>
      <div className="decision-insights-content classifying-normal-decision-insights-content">
        <PopupMessages
          messages={popupMessages}
          messageSize="xx-small"
          buttonText="Next"
          onButtonClick={handleNextButtonClick}
          additionalButtonText="Back"
          onAdditionalButtonClick={handlePrevButtonClick}
          isAvatarOnTop={true}
        />
        <Scores
          labels={labels}
          data={data}
          size="small"
          maxScore={maxScore}
          alignBarTitle="center"
          detailsButtonText={detailsButtonText}
          onDetailsButtonClick={handleDetailsButtonClick}
          isDetailsButtonLoading={isDetailsButtonLoading}
        />
        {isModalShown && (
          <TopMisclassifiedAccountsModal
            onCloseButtonClick={handleModalCloseButtonClick}
            classificationInsights={classificationInsights}
            normalInsights={normalInsights}
          />
        )}
      </div>
    </SubPagesLayout>
  );
}
